import { Tooltip } from "@/components/ui/Tooltip";
import {
  RadioGroup as RadioGroupPrimitive,
  type RadioGroupProps as RadioGroupPrimitiveProps,
} from "@radix-ui/react-radio-group";
import "./RadioGroup.css";

import React from "react";

import { cn } from "@/lib/utils";

import { RadioGroupItem } from "./RadioGroupItem";

interface RadioGroupOption {
  label: string | React.ReactNode;
  value: string;
  optionDisabled?: boolean;
  tooltipText?: string;
  tooltipClassName?: string;
}

interface RadioGroupProps {
  options: RadioGroupOption[];
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive>,
  React.ComponentPropsWithRef<typeof RadioGroupPrimitive> &
    RadioGroupPrimitiveProps &
    RadioGroupProps
>(({ options, orientation = "horizontal", disabled, name, ...props }, ref) => (
  <div className="flex">
    <RadioGroupPrimitive
      className={cn(
        "flex",
        orientation === "horizontal"
          ? "flex-row radio-group-horizontal"
          : "flex-col radio-group-vertical",
      )}
      ref={ref}
      name={name}
      {...props}
    >
      {options.map(
        ({ label, value, optionDisabled, tooltipText, tooltipClassName }) => {
          const item = (
            <RadioGroupItem
              id={`${name}-${value}`}
              label={label}
              value={value}
              disabled={disabled || optionDisabled}
            />
          );

          const maybeWrappedWithTooltip = tooltipText ? (
            <Tooltip
              tooltipContentText={tooltipText}
              contentClassName={tooltipClassName}
            >
              {item}
            </Tooltip>
          ) : (
            item
          );

          // Ensure a stable immediate child wrapper for consistent reconciliation and spacing
          return (
            <span key={`${name || "radio-group"}-${value}`}>
              {maybeWrappedWithTooltip}
            </span>
          );
        },
      )}
    </RadioGroupPrimitive>
  </div>
));

RadioGroup.displayName = RadioGroupPrimitive.displayName;

export { RadioGroup, type RadioGroupOption };
